import {Component, EventEmitter, NgZone, OnDestroy, OnInit, Output} from '@angular/core';
import {BiometryVendor} from '@npck/auth-frontend-api';
import {Spinner} from '@npck/libs/commons';
import {Observable, Subscription} from 'rxjs';
import {AuthHolderService} from '../../domain/auth-holder.service';
import {LivenessService} from '../../domain/liveness.service';
import {Liveness} from '../../domain/liveness/liveness';
import {FaceTecLivenessVendorService} from '../../domain/liveness/facetec/facetec-liveness-vendor.service';
import {OzLivenessVendorService} from '../../domain/liveness/ozforensics/oz-liveness-vendor.service';
import {ScriptLoaderService} from '../../domain/liveness/utils/script-loader.service';
import {LivenessServiceResponse, LivenessServiceStatus} from '../../pages/liveness-device/liveness-device.component';

@Component({
    selector: 'auth-liveness',
    templateUrl: './liveness.component.html',
    standalone: false,
    providers: [FaceTecLivenessVendorService, OzLivenessVendorService, ScriptLoaderService],
})
export class LivenessComponent implements OnInit, OnDestroy {
    @Output() livenessResponseEvent: EventEmitter<LivenessServiceResponse> = new EventEmitter();

    Spinner = Spinner;
    timeoutReached = false;

    private vendorService: Liveness;
    private subscription: Subscription;
    private destroyed = false;

    constructor(
        private authHolderService: AuthHolderService,
        private faceTecService: FaceTecLivenessVendorService,
        private ozService: OzLivenessVendorService,
        private livenessService: LivenessService,
        private ngZone: NgZone,
    ) {
        this.vendorService = this.resolveVendor(this.authHolderService.biometryVendor);
    }

    ngOnInit(): void {
        Spinner.show('loading');

        if (this.authHolderService.skipLiveness) {
            this.subscribe(this.vendorService.skipLiveness());
        } else {
            void this.checkCameraAvailability().then(cameraAvailable => {
                this.ngZone.run(() => {
                    if (this.destroyed) {
                        return;
                    }
                    if (!cameraAvailable) {
                        Spinner.hide('loading');
                        this.livenessResponseEvent.emit({
                            status: LivenessServiceStatus.CAMERA_FAILED,
                            rootStatus: 'CAMERA_NOT_FOUND',
                        });
                        return;
                    }
                    this.vendorService.init();
                    this.subscribe(this.vendorService.startLiveness());
                });
            });
        }
    }

    ngOnDestroy(): void {
        this.destroyed = true;
        this.subscription?.unsubscribe();
    }

    private async checkCameraAvailability(): Promise<boolean> {
        if (!navigator.mediaDevices?.getUserMedia) {
            return true;
        }
        try {
            const stream = await navigator.mediaDevices.getUserMedia({video: true});
            stream.getTracks().forEach(track => track.stop());
            return true;
        } catch (error) {
            this.livenessService.addBreadcrumb('Camera pre-check failed', {name: (error as DOMException)?.name});
            return false;
        }
    }

    private subscribe(source: Observable<LivenessServiceResponse>): void {
        this.subscription?.unsubscribe();
        this.subscription = source.subscribe(response => {
            Spinner.hide('loading');
            this.livenessResponseEvent.emit(response);
        });
    }

    private resolveVendor(vendor: BiometryVendor): Liveness {
        switch (vendor) {
            case BiometryVendor.ZOOM:
                return this.faceTecService;
            case BiometryVendor.OZFORENSICS:
                return this.ozService;
        }
    }
}
